import type { PickerAgent } from "./MentionList";

/**
 * Pure recipient logic of "An Kollegen senden" (#717, UI-SPEC section 3). The client side is a
 * PREVIEW: the server re-resolves every typed address at send time (D-01), so nothing here is an
 * authority, it only tells the agent what is about to happen. No React.
 */

/** The cap of a single send (same as mentions). */
export const MAX_FORWARD_RECIPIENTS = 20;

/**
 * What a chip shows: `lytra` (a colleague with an account), `mail` (leaves Lytra), `checking`
 * (the agents list has not answered yet), `unknown` (the lookup failed, so no claim is made).
 */
export type RecipientChannel = "lytra" | "mail" | "checking" | "unknown";

/**
 * One recipient chip. A picked colleague has `userId` and (when known) `address`; a typed
 * address has `address` only. `label` is what was typed or picked, the chip shows the
 * classified label (a Lytra match shows the full name).
 */
export interface RecipientEntry {
  key: string;
  userId: string | null;
  address: string | null;
  label: string;
}

export type AgentsState = "loading" | "ready" | "error";

export interface ClassifiedRecipient {
  channel: RecipientChannel;
  /** A "per Mail" address outside the tenant's domains (D-10). Never set without a domain list. */
  foreign: boolean;
  label: string;
  userId: string | null;
}

export interface RecipientInput {
  userId?: string;
  address?: string;
}

const PIECE_SEPARATOR = /[\s,;]+/;
// One "@", a local part, a dotted domain with no empty label, none of whitespace and "<>,;".
const ADDRESS_SHAPE = /^[^\s@<>,;]+@(?:[^\s@<>,;.]+\.)+[^\s@<>,;.]+$/;

/** Pieces of a typed or pasted text: split on commas, semicolons, whitespace and newlines. */
export function splitAddressInput(text: string): string[] {
  return text.split(PIECE_SEPARATOR).filter((piece) => piece !== "");
}

/** A plain `local@domain.tld` shape check; the server is the authority. */
export function isAddressShaped(text: string): boolean {
  return ADDRESS_SHAPE.test(text.trim());
}

/** The part after the last "@", lower-cased; null when there is none. */
export function addressDomain(address: string): string | null {
  const at = address.lastIndexOf("@");
  if (at < 0) return null;
  const domain = address
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return domain === "" ? null : domain;
}

/**
 * True when the address is not on one of the tenant's own domains. Exact match, a subdomain
 * does not count as the company's (as the backend does). With no domains configured, or not
 * loaded, nothing is foreign: there is nothing to compare against.
 */
export function isForeignAddress(
  address: string,
  internalDomains: readonly string[] | null | undefined,
): boolean {
  if (!internalDomains || internalDomains.length === 0) return false;
  const domain = addressDomain(address);
  if (domain === null) return false;
  return !internalDomains.some((own) => own.trim().toLowerCase() === domain);
}

const normalizeAddress = (address: string) => address.trim().toLowerCase();

/** The agent whose `emailAddress` equals the address (trimmed, case-insensitive). */
export function matchAgentByAddress(
  agents: readonly PickerAgent[],
  address: string | null | undefined,
): PickerAgent | null {
  if (!address) return null;
  const wanted = normalizeAddress(address);
  if (wanted === "") return null;
  return (
    agents.find(
      (agent) =>
        agent.emailAddress != null &&
        normalizeAddress(agent.emailAddress) === wanted,
    ) ?? null
  );
}

/** The full name of an agent; an agent with no name falls back to the address. */
export function agentName(agent: PickerAgent): string {
  const full = `${agent.givenName ?? ""} ${agent.familyName ?? ""}`.trim();
  return full || (agent.emailAddress ?? "");
}

/**
 * What the chip of an entry shows. A userId or a matching agent address is a Lytra
 * recipient (the match wins over the domain check, D-06); while the agents list loads the
 * channel is unknown-yet (`checking`), when it failed `unknown`; the rest goes by mail.
 */
export function classifyRecipient(
  entry: RecipientEntry,
  agents: readonly PickerAgent[],
  agentsState: AgentsState,
  internalDomains: readonly string[] | null | undefined,
): ClassifiedRecipient {
  if (entry.userId) {
    const agent = agents.find((candidate) => candidate.id === entry.userId);
    return {
      channel: "lytra",
      foreign: false,
      label: agent ? agentName(agent) : entry.label,
      userId: entry.userId,
    };
  }
  const label = entry.address ?? entry.label;
  if (agentsState === "loading") {
    return { channel: "checking", foreign: false, label, userId: null };
  }
  if (agentsState === "error") {
    return { channel: "unknown", foreign: false, label, userId: null };
  }
  const agent = matchAgentByAddress(agents, entry.address);
  if (agent) {
    return {
      channel: "lytra",
      foreign: false,
      label: agentName(agent),
      userId: agent.id,
    };
  }
  return {
    channel: "mail",
    foreign: entry.address
      ? isForeignAddress(entry.address, internalDomains)
      : false,
    label,
    userId: null,
  };
}

export interface AddRecipientContext {
  viewerId?: string;
  viewerEmail?: string | null;
  agents: readonly PickerAgent[];
  /** False while sending by mail is off: only colleagues with an account can be added. */
  mailEnabled?: boolean;
}

export interface AddRecipientResult {
  list: RecipientEntry[];
  /** `noAccount`: an address of nobody with a Lytra account while sending by mail is off. */
  error?: "self" | "cap" | "invalid" | "noAccount";
  /** Set when the candidate was already in the list: nothing was added. */
  duplicateOf?: RecipientEntry;
}

export interface RecipientCandidate {
  userId?: string | null;
  address?: string | null;
  label?: string;
}

/** The Lytra user an entry stands for, by id or by a matching address. */
function identityOf(
  entry: { userId?: string | null; address?: string | null },
  agents: readonly PickerAgent[],
): string | null {
  if (entry.userId) return entry.userId;
  return matchAgentByAddress(agents, entry.address)?.id ?? null;
}

/**
 * Add a recipient, or say why not. Order: an unshaped typed address ("invalid"), the viewer
 * themself ("self"), an address of nobody with an account while mail is off ("noAccount"; while
 * the agents list loads no address matches, so none is taken), a duplicate (same Lytra user, or the same address in any case: nothing
 * is added and `duplicateOf` names the chip already there), the 21st entry ("cap"). Pure: the
 * input list is never changed.
 */
export function addRecipient(
  list: readonly RecipientEntry[],
  candidate: RecipientCandidate,
  { viewerId, viewerEmail, agents, mailEnabled = true }: AddRecipientContext,
): AddRecipientResult {
  const userId = candidate.userId ?? null;
  const rawAddress = candidate.address?.trim() || null;
  const unchanged = [...list];

  if (!userId && (rawAddress === null || !isAddressShaped(rawAddress))) {
    return { list: unchanged, error: "invalid" };
  }

  const candidateAgentId = identityOf({ userId, address: rawAddress }, agents);
  const isViewer =
    (viewerId !== undefined && candidateAgentId === viewerId) ||
    (!!viewerEmail &&
      rawAddress !== null &&
      normalizeAddress(rawAddress) === normalizeAddress(viewerEmail));
  if (isViewer) return { list: unchanged, error: "self" };
  if (!mailEnabled && candidateAgentId === null) {
    return { list: unchanged, error: "noAccount" };
  }

  const wantedAddress = rawAddress ? normalizeAddress(rawAddress) : null;
  const duplicateOf = list.find((entry) => {
    if (
      candidateAgentId !== null &&
      identityOf(entry, agents) === candidateAgentId
    ) {
      return true;
    }
    return (
      wantedAddress !== null &&
      entry.address !== null &&
      normalizeAddress(entry.address) === wantedAddress
    );
  });
  if (duplicateOf) return { list: unchanged, duplicateOf };

  if (list.length >= MAX_FORWARD_RECIPIENTS) {
    return { list: unchanged, error: "cap" };
  }

  const entry: RecipientEntry = userId
    ? {
        key: `user:${userId}`,
        userId,
        address: rawAddress,
        label: candidate.label?.trim() || rawAddress || "",
      }
    : {
        key: `address:${normalizeAddress(rawAddress!)}`,
        userId: null,
        address: rawAddress,
        label: rawAddress!,
      };
  return { list: [...unchanged, entry] };
}

/** How many chips go which way. `checking` counts as unknown: no claim is made yet. */
export function channelSummary(
  classified: readonly Pick<ClassifiedRecipient, "channel">[],
): { lytra: number; mail: number; unknown: number } {
  const counts = { lytra: 0, mail: 0, unknown: 0 };
  for (const { channel } of classified) {
    if (channel === "lytra") counts.lytra += 1;
    else if (channel === "mail") counts.mail += 1;
    else counts.unknown += 1;
  }
  return counts;
}

/** The mutation's recipients: the userId for a Lytra entry, the address for every other. */
export function toRecipientInputs(
  list: readonly RecipientEntry[],
  agents: readonly PickerAgent[],
): RecipientInput[] {
  return list.map((entry) => {
    const userId = identityOf(entry, agents);
    if (userId) return { userId };
    return { address: entry.address ?? entry.label };
  });
}
